// Keyboard shortcuts of the source viewer on one window listener: + − 0 zoom and fit, Page Down and Page Up turn pages; only the most recently mounted viewer answers, and only while focus is inside it or on the page itself (WCAG 2.1.4), never in fields, dialogs or during a g sequence.
import { useEffect, useEffectEvent, useId } from "react";

import { goSequencePending, shouldIgnoreSingleKey } from "@/app/shortcuts";

export type ViewerKeyAction = "zoomIn" | "zoomOut" | "fit" | "next" | "previous";

const KEY_ACTIONS: Record<string, ViewerKeyAction> = {
  "+": "zoomIn",
  "=": "zoomIn",
  "-": "zoomOut",
  _: "zoomOut",
  "0": "fit",
  PageDown: "next",
  PageUp: "previous",
};

const mounted: string[] = [];

export function viewerKeyAction(event: KeyboardEvent, root: Element | null): ViewerKeyAction | null {
  if (
    event.defaultPrevented ||
    event.ctrlKey ||
    event.metaKey ||
    event.altKey ||
    shouldIgnoreSingleKey(event)
  ) {
    return null;
  }
  if (goSequencePending()) {
    return null;
  }
  const action = KEY_ACTIONS[event.key];
  if (action === undefined) {
    return null;
  }
  const focused = document.activeElement;
  const pageLevel = focused === document.body || (focused?.id ?? "main") === "main";
  return pageLevel || root?.contains(focused) === true ? action : null;
}

export function useViewerHotkeys(root: Element | null, handlers: Record<ViewerKeyAction, () => void>): void {
  const id = useId();
  const onKeyDown = useEffectEvent((event: KeyboardEvent) => {
    if (mounted.at(-1) !== id) {
      return;
    }
    const action = viewerKeyAction(event, root);
    if (action === null) {
      return;
    }
    event.preventDefault();
    handlers[action]();
  });
  useEffect(() => {
    mounted.push(id);
    const listener = (event: KeyboardEvent) => {
      onKeyDown(event);
    };
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
      const index = mounted.lastIndexOf(id);
      if (index >= 0) {
        mounted.splice(index, 1);
      }
    };
  }, [id]);
}
